import React, { useState } from 'react';
import { colors, fonts, spacing, radii, layout } from '../styles/theme';

interface NamesScreenProps {
    onContinue: (firstPerson: string, secondPerson: string) => void;
    onBackToLanding: () => void;
    initialFirstPerson?: string;
    initialSecondPerson?: string;
}

const NamesScreen: React.FC<NamesScreenProps> = ({
    onContinue,
    onBackToLanding,
    initialFirstPerson = '',
    initialSecondPerson = '',
}) => {
    const [firstPerson, setFirstPerson] = useState(initialFirstPerson);
    const [secondPerson, setSecondPerson] = useState(initialSecondPerson);

    const canContinue = firstPerson.trim().length > 0 && secondPerson.trim().length > 0;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canContinue) return;
        onContinue(firstPerson.trim(), secondPerson.trim());
    };

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <button
                    type="button"
                    style={styles.backButton}
                    onClick={onBackToLanding}
                >
                    ← Back to the beginning
                </button>

                <h1 style={styles.title}>Who is using the tool today?</h1>
                <p style={styles.subtitle}>
                    This works best as a conversation between two people. Add your names and you will see them above your characters.
                </p>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.fieldRow}>
                        <div style={styles.field}>
                            <label htmlFor="firstPerson" style={styles.label}>
                                First person
                            </label>
                            <input
                                id="firstPerson"
                                type="text"
                                value={firstPerson}
                                onChange={(e) => setFirstPerson(e.target.value)}
                                placeholder="Type a name…"
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.field}>
                            <label htmlFor="secondPerson" style={styles.label}>
                                Second person
                            </label>
                            <input
                                id="secondPerson"
                                type="text"
                                value={secondPerson}
                                onChange={(e) => setSecondPerson(e.target.value)}
                                placeholder="Type a name…"
                                style={styles.input}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        style={{
                            ...styles.continueButton,
                            ...(!canContinue ? styles.continueButtonDisabled : {}),
                        }}
                        disabled={!canContinue}
                    >
                        Continue
                    </button>
                    <p style={styles.disclaimer}>
                        We do not collect any data. The names you enter are only used within this session and are not stored.
                    </p>
                </form>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bgLight,
        padding: spacing[6],
    },
    content: {
        width: '100%',
        maxWidth: layout.contentWidth,
    },
    backButton: {
        marginBottom: spacing[4],
        padding: `${spacing[1]} ${spacing[2]}`,
        border: 'none',
        background: 'none',
        color: colors.textSecondary,
        fontSize: fonts.sm,
        cursor: 'pointer',
        textDecoration: 'underline',
        textAlign: 'left' as const,
    },
    title: {
        fontSize: fonts['2xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[2],
        textAlign: 'left' as const,
    },
    subtitle: {
        fontSize: fonts.base,
        color: colors.textSecondary,
        marginBottom: spacing[6],
        maxWidth: 600,
    },
    form: {
        backgroundColor: colors.white,
        borderRadius: radii.xl,
        border: `1px solid ${colors.border}`,
        padding: spacing[8],
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: spacing[4],
        marginBottom: spacing[6],
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[1],
    },
    label: {
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.text,
    },
    input: {
        padding: `${spacing[3]} ${spacing[3]}`,
        fontSize: fonts.base,
        borderRadius: radii.md,
        border: `1px solid ${colors.border}`,
        outline: 'none',
    },
    continueButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[4]} ${spacing[6]}`,
        fontSize: fonts.lg,
        fontWeight: fonts.bold,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.lg,
        cursor: 'pointer',
        minHeight: layout.minTouchTarget,
        boxShadow: 'none',
    },
    continueButtonDisabled: {
        backgroundColor: colors.textMuted,
        cursor: 'not-allowed',
        opacity: 0.6,
        boxShadow: 'none',
    },
    disclaimer: {
        marginTop: spacing[3],
        fontSize: fonts.xs,
        color: colors.textMuted,
        textAlign: 'center' as const,
    },
};

export default NamesScreen;

